<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleUpdateOpen"
  >
    <DSDialogContent class="gap-0">
      <DSDialogHeader class="border-b border-border">
        <DSDialogTitle class="text-xl">
          Connect {{ name }}
        </DSDialogTitle>
        <DSDialogDescription>
          Allposit reads this account through the Akoya Data Access Network.
        </DSDialogDescription>
      </DSDialogHeader>

      <div class="flex flex-col items-center gap-4 px-6 py-6">
        <!--
          Institution artwork is drawn for light grounds, so its plate stays
          white in both themes rather than following the card surface.
        -->
        <span class="flex items-center justify-center h-20 w-full p-2 bg-white rounded-ds-md">
          <img
            :src="logoSrc"
            :alt="name"
            class="h-full max-w-full object-contain"
            @error="handleLogoError"
          >
        </span>

        <label class="flex items-start gap-2.5 cursor-pointer select-none">
          <DSCheckbox
            v-model="agreement"
            class="mt-0.5"
          />
          <span class="text-xs font-medium leading-normal text-muted-foreground">
            I have read and agreed to the following terms of the account information
            sharing. Account information is retrieved via Akoya Data Access Network.
            The information includes account balances and positions. The information
            is retrieve only. The information will not be shared, rented, exchanged
            or sold without your consent.
          </span>
        </label>
      </div>

      <DSDialogFooter class="bg-background border-t border-border">
        <DSButton
          variant="secondary"
          @click="handleCancel"
        >
          Cancel
        </DSButton>
        <DSButton
          :disabled="isDisabled"
          @click="handleNext"
        >
          Next
        </DSButton>
      </DSDialogFooter>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { ModalClass } from '@types'

import { INSTITUTION_LOGO_PLACEHOLDER } from '@/views/LinkedData/utils/const'

import {
  DSButton,
  DSCheckbox,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogFooter,
  DSDialogHeader,
  DSDialogTitle,
} from '@ds'

type Props = {
  modal: ModalClass<any>
  name: string
  url: string
  logo?: string | null
}

type Emits = {
  cancel: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const agreement = ref(false)

const isOpen = computed(() => props.modal.isOpened)

const isDisabled = computed(() => !agreement.value || !props.url)

const logoSrc = ref<string>(props.logo || INSTITUTION_LOGO_PLACEHOLDER)

const handleLogoError = () => {
  logoSrc.value = INSTITUTION_LOGO_PLACEHOLDER
}

const handleClose = () => {
  props.modal.close()
  agreement.value = false
}

const handleUpdateOpen = (value: boolean) => {
  // Reka reports Esc, scrim click and the close button the same way the old
  // dialog reported its close — and both mean "cancel", so the hand-off unwinds
  // and the caller can re-show whatever it came from.
  if (!value) handleCancel()
}

const handleCancel = () => {
  handleClose()
  emit('cancel')
}

const handleNext = () => {
  if (!props.url) return
  location.href = props.url
}
</script>
